// src/components/analytics/InsightsPanel.js

import React, { useState } from "react";
import { Star } from "lucide-react";
import { AnimatePresence } from "framer-motion";
import DailyCard from "./DailyCard";
import WeekCard from "./WeekCard";
import MonthCard from "./MonthCard";
import YearCard from "./YearCard";
import { useLang } from "../../contexts/LanguageContext";
import locales from "../../utils/locales";
import parseDuration from "../../utils/parseDuration";
import MiniMonthBar from "../MiniMonthBar";
import {
  startOfDay,
  startOfWeek,
  startOfMonth,
  isSameDay,
  isSameWeek,
  isSameMonth,
  format,
} from "date-fns";

// --- Premium Lock UI ---
function PremiumLock() {
  return (
    <div className="relative min-h-[350px] flex items-center justify-center">
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 text-center">
        <span className="text-3xl font-extrabold text-yellow-600 mb-2">
          Premium Only
        </span>
        <p className="mb-5 text-gray-600 text-lg">
          ปลดล็อก Advanced Insights<br />ด้วย Spendora Premium
          <span className="block mt-3 text-sm text-gray-500">
            • เปรียบเทียบหมวดหมู่ย้อนหลัง<br />
            • วิเคราะห์แนวโน้ม & export ข้อมูล
          </span>
        </p>
        <button
          className="px-7 py-2 rounded-xl bg-yellow-400 text-white font-bold text-lg shadow hover:bg-yellow-500 transition"
          onClick={() => window.open("/subscribe", "_self")}
        >
          อัปเกรดตอนนี้
        </button>
      </div>
      <div className="absolute inset-0 bg-white bg-opacity-70 backdrop-blur-[2px]" />
    </div>
  );
}

// --- Helper: ให้ได้ Date object (local, date-only) ---
function getDateObj(a) {
  if (a.activity_date) return new Date(a.activity_date);
  if (a.created_at) return new Date(a.created_at);
  return null;
}
function toLocalDateOnly(date) {
  if (!date) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// --- สรุปรายวันแบบ reusable ---
function summarizeDay(activities, categories, lang) {
  if (!activities || activities.length === 0) return null;
  const totalMin = activities.reduce(
    (sum, a) => sum + parseDuration(a.duration),
    0
  );
  const totalHour = totalMin / 60;

  const catCount = {};
  activities.forEach((a) => {
    catCount[a.category] =
      (catCount[a.category] || 0) + parseDuration(a.duration || 0);
  });
  const topCatVal = Object.entries(catCount).sort((a, b) => b[1] - a[1])[0]?.[0];
  const topCategory =
    categories.find((c) => c.value === topCatVal)?.label?.[lang] || "-";

  const actCount = {};
  activities.forEach((a) => {
    actCount[a.activity] =
      (actCount[a.activity] || 0) + parseDuration(a.duration || 0);
  });
  const topAct =
    Object.entries(actCount).sort((a, b) => b[1] - a[1])[0]?.[0] || "-";

  const moodToScore = { "😊": 5, "😌": 4, "😐": 3, "😔": 2, "😠": 1 };
  const scoreToMood = [null, "😠", "😔", "😐", "😌", "😊"];
  const moodArr = activities.map((a) => moodToScore[a.mood]).filter(Boolean);
  const avgMood =
    moodArr.length > 0
      ? scoreToMood[Math.round(moodArr.reduce((a, b) => a + b, 0) / moodArr.length)]
      : "-";

  const activityCount = activities.length;
  const activityCountPerCat = categories.map((cat) => ({
    label: cat.label[lang],
    value: activities.filter((a) => a.category === cat.value).length,
  }));

  return {
    totalHour,
    topCategory,
    topActivity: topAct,
    avgMood,
    activityCount,
    activityCountPerCat,
  };
}

export default function InsightsPanel({
  user,
  activities = [],
  categories = [],
}) {
  const { lang } = useLang();
  const isPremium =
    user &&
    (user.is_premium === true ||
      user.plan === "premium" ||
      user.role === "premium");
  const today = new Date();
  const [mode, setMode] = useState("day");
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(startOfMonth(today));
  const [week, setWeek] = useState(startOfWeek(today, { weekStartsOn: 1 }));
  const [day, setDay] = useState(startOfDay(today));

  if (!isPremium) return <PremiumLock />;

  // --- Filter Activities ด้วย date-local-only ---
  let filteredActivities = [];
  if (mode === "day") {
    filteredActivities = activities.filter((a) =>
      isSameDay(
        toLocalDateOnly(getDateObj(a)),
        toLocalDateOnly(day)
      )
    );
  } else if (mode === "week") {
    filteredActivities = activities.filter((a) =>
      isSameWeek(
        toLocalDateOnly(getDateObj(a)),
        toLocalDateOnly(week),
        { weekStartsOn: 1 }
      )
    );
  } else if (mode === "month") {
    filteredActivities = activities.filter((a) =>
      isSameMonth(
        toLocalDateOnly(getDateObj(a)),
        toLocalDateOnly(month)
      )
    );
  } else if (mode === "year") {
    filteredActivities = activities.filter(
      (a) => toLocalDateOnly(getDateObj(a))?.getFullYear() === year
    );
  }

  // --- Render cardEl ตาม mode ---
  let cardEl = null;
  if (mode === "day") {
    const summary = summarizeDay(filteredActivities, categories, lang);
    cardEl = summary ? (
      <DailyCard
        date={format(day, lang === "th" ? "dd MMM yyyy" : "MMM dd, yyyy")}
        summary={summary}
        activities={filteredActivities}
        categories={categories}
        lang={lang}
      />
    ) : (
      <div className="text-gray-400 text-center py-10">
        {lang === "th" ? "ยังไม่มีกิจกรรม" : "No activity yet."}
      </div>
    );
  } else if (mode === "week") {
    cardEl = (
      <WeekCard
        categories={categories}
        activities={filteredActivities}
        lang={lang}
        week={week}
      />
    );
  } else if (mode === "month") {
    cardEl = (
      <MonthCard
        categories={categories}
        activities={filteredActivities}
        lang={lang}
        month={month}
        allActivities={activities}
      />
    );
  } else if (mode === "year") {
    cardEl = (
      <YearCard
        categories={categories}
        activities={filteredActivities}
        lang={lang}
        yearLabel={lang === "th" ? `ปี ${year}` : `Year ${year}`}
      />
    );
  }

  // --- Available Years (for MiniMonthBar) ---
  const availableYears = Array.from(
    new Set(
      activities
        .map((a) => toLocalDateOnly(getDateObj(a))?.getFullYear())
        .filter((y) => !!y)
    )
  ).sort();

  return (
    <div className="w-full max-w-2xl mx-auto px-2 py-5 bg-white/90 rounded-3xl shadow-2xl border border-gray-50 mb-10">
      {/* Headline */}
      <div className="flex items-center gap-3 justify-center mb-2">
        <span className="bg-yellow-100 rounded-full p-2">
          <Star className="text-yellow-500" size={30} />
        </span>
        <h2 className="text-2xl md:text-3xl font-extrabold text-yellow-600 drop-shadow">
          {locales[lang]?.advanced_insights || "Advanced Insights"}
        </h2>
      </div>

      {/* MiniMonthBar */}
      <MiniMonthBar
        mode={mode}
        setMode={setMode}
        availableYears={availableYears}
        year={year}
        setYear={setYear}
        month={month}
        setMonth={setMonth}
        week={week}
        setWeek={setWeek}
        day={day}
        setDay={setDay}
      />

      {/* Animated slide card */}
      <div className="min-h-[350px] flex flex-col justify-center items-stretch">
        <AnimatePresence mode="wait">{cardEl}</AnimatePresence>
      </div>
    </div>
  );
}